import { useState } from 'react';
import { Check, ArrowRight } from 'lucide-react';

const APP_URL = 'https://app.theonestack.com';
const AMS_PATH = '/ams';

const tiers = [
  {
    name: 'Starter',
    monthly: 49,
    annual: 41,
    desc: 'For small associations and HOAs just getting started.',
    features: [
      'Up to 500 members',
      'Member directory & profiles',
      'Event management (5/month)',
      'Basic dues & invoicing',
      'Email communications',
      'Community forum',
      'Email support',
    ],
    popular: false,
  },
  {
    name: 'Professional',
    monthly: 149,
    annual: 124,
    desc: 'Full-featured for growing organizations with active programs.',
    features: [
      'Up to 5,000 members',
      'Everything in Starter',
      'Unlimited events',
      'Committee & governance tools',
      'Learning Management System',
      'Advanced financial reporting',
      'SMS communications',
      'Advocacy tools',
      'Phone add-on available',
      'Priority support',
    ],
    popular: true,
  },
  {
    name: 'Enterprise',
    monthly: 349,
    annual: 291,
    desc: 'Unlimited scale, dedicated support, and API access.',
    features: [
      'Unlimited members',
      'Everything in Professional',
      'Multi-chapter / multi-branch',
      'Custom integrations & API',
      'SSO / SCIM provisioning',
      'Dedicated success manager',
      'SLA guarantee',
      'White-label options',
      'Data migration assistance',
    ],
    popular: false,
  },
];

export function PricingSection() {
  const [annual, setAnnual] = useState(false);

  return (
    <section id="pricing" className="relative py-28 sm:py-32">
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background: 'linear-gradient(180deg, rgba(10,15,13,0) 0%, rgba(4,47,46,0.12) 50%, rgba(10,15,13,0) 100%)',
        }}
      />

      <div className="relative mx-auto max-w-6xl px-6">
        <div className="mb-16 text-center reveal">
          <span className="mb-4 inline-block rounded-full border border-ams-700/40 bg-ams-950/40 px-3 py-1 text-xs font-medium text-ams-400">
            Pricing
          </span>
          <h2 className="mb-4 text-3xl font-bold tracking-tight sm:text-4xl">
            Transparent Pricing, No Surprises
          </h2>
          <p className="mx-auto mb-8 max-w-2xl text-zinc-400">
            Start free for 14 days. No credit card required. Cancel anytime.
          </p>

          {/* Billing toggle */}
          <div className="flex items-center justify-center gap-3">
            <span className={`text-sm ${!annual ? 'text-zinc-100' : 'text-zinc-500'}`}>Monthly</span>
            <button
              className={`billing-toggle ${annual ? 'active' : ''}`}
              onClick={() => setAnnual(!annual)}
              aria-label="Toggle annual billing"
            />
            <span className={`text-sm ${annual ? 'text-zinc-100' : 'text-zinc-500'}`}>
              Annual
            </span>
            {annual && (
              <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-xs font-medium text-emerald-400">
                2 months free
              </span>
            )}
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          {tiers.map((tier) => (
            <div
              key={tier.name}
              className={`reveal relative rounded-2xl border p-8 ${
                tier.popular
                  ? 'pricing-popular border-ams-500/40 bg-ams-950/40'
                  : 'border-ams-800/30 bg-ams-950/20'
              }`}
            >
              {tier.popular && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-ams-500 px-3 py-1 text-xs font-medium text-white">
                  Most Popular
                </span>
              )}
              <h3 className="mb-1 text-xl font-bold">{tier.name}</h3>
              <p className="mb-6 text-sm text-zinc-400">{tier.desc}</p>
              <div className="mb-6">
                <span className="text-4xl font-bold">
                  ${annual ? tier.annual : tier.monthly}
                </span>
                <span className="text-zinc-400">/mo</span>
                {annual && (
                  <span className="ml-2 text-xs text-zinc-600 line-through">
                    ${tier.monthly}/mo
                  </span>
                )}
              </div>
              <ul className="mb-8 space-y-3">
                {tier.features.map((feat) => (
                  <li key={feat} className="flex items-start gap-2 text-sm text-zinc-300">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-ams-400" />
                    {feat}
                  </li>
                ))}
              </ul>
              <a
                href={`${APP_URL}${AMS_PATH}`}
                className={`group flex w-full items-center justify-center gap-2 rounded-lg py-3 text-sm font-semibold transition ${
                  tier.popular
                    ? 'bg-ams-500 text-white hover:bg-ams-600'
                    : 'border border-ams-700/40 text-zinc-300 hover:bg-ams-950/60'
                }`}
              >
                Start Free Trial
                <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
              </a>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
